import axios, { AxiosResponse } from 'axios'
import httpService from '../httpService'
import { NSDCAccreditation } from '../types/NsdcAccreditation'

export type NsdcFileType = 'thumbnail' | 'curriculum' | 'agreement'

export interface NsdcUploadUrlParams {
    fileType: NsdcFileType
    extension: string
    contentType?: string
}

export interface NsdcUploadUrlResponse {
    uploadData: {
        url: string
        fields: Record<string, string>
    }
    publicUrl: string
    key: string
    fileType: NsdcFileType
}

/**
 * Gets a presigned S3 upload URL for NSDC accreditation file uploads
 */
export function getNsdcUploadUrl(
    params: NsdcUploadUrlParams,
): Promise<AxiosResponse<NsdcUploadUrlResponse>> {
    return httpService.get('/v9/nsdc-accreditation-records/get-upload-url/', {
        params,
    })
}

/**
 * Uploads a file directly to S3 using presigned URL data
 */
export function uploadFileToS3(
    url: string,
    fields: Record<string, string>,
    file: File,
    onUploadProgress?: (progressEvent: ProgressEvent) => void,
): Promise<AxiosResponse> {
    const formData = new FormData()
    Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value)
    })
    formData.append('file', file)

    return axios.post(url, formData, { onUploadProgress })
}

/**
 * Fetches a list of NSDC accreditation records based on the provided filter
 */
export function getNSDCAccreditation(
    filter: NSDCAccreditation.Filter,
): Promise<AxiosResponse<NSDCAccreditation.PaginatedResponse<NSDCAccreditation.Root>>> {
    return httpService.get(`/v9/nsdc-accreditation-records`, {
        params: filter,
    })
}

/**
 * Creates a new NSDC accreditation record
 */
export function createNSDCAccreditation(
    nsdcAccreditation: NSDCAccreditation.Root,
): Promise<AxiosResponse<NSDCAccreditation.Root>> {
    return httpService.post(`/v9/nsdc-accreditation-records/`, nsdcAccreditation)
}

/**
 * Fetches a single NSDC accreditation record by ID
 */
export function getNSDCAccreditationById(
    id: string | number,
): Promise<AxiosResponse<NSDCAccreditation.Root>> {
    return httpService.get(`/v9/nsdc-accreditation-records/${id}/`)
}


/**
 * Updates an existing NSDC accreditation record
 */
export function updateNSDCAccreditationRecord(
    id: number,
    nsdcAccreditation: Partial<NSDCAccreditation.Root>,
): Promise<AxiosResponse<NSDCAccreditation.Root>> {
    return httpService.patch(`/v9/nsdc-accreditation-records/${id}`, nsdcAccreditation)
}



/**
 * Updates an existing NSDC accreditation record status (PATCH request)
 */
export function updateNSDCAccreditationStatus(
    id: number,
    nsdcAccreditation: Partial<NSDCAccreditation.Root>,
): Promise<AxiosResponse<NSDCAccreditation.Root>> {
    return httpService.patch(`/v9/nsdc-accreditation-records/${id}/status-change`, nsdcAccreditation)
}